/**
 * Pure helpers for phone runtime release changelog (no Vite-only APIs).
 */

export type PhoneReleaseManifest = {
  version: string;
  notes: string;
  /** ISO date or datetime when present (older manifests use createdAt). */
  date: string | null;
  validatorId: string | null;
  locales: string[] | null;
  /** Raw manifest fields for display. */
  raw: Record<string, unknown>;
};

export type RawPhoneReleaseManifest = {
  version?: unknown;
  notes?: unknown;
  releasedAt?: unknown;
  createdAt?: unknown;
  validatorId?: unknown;
  locales?: unknown;
};

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function asStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const out = value.filter(
    (item): item is string => typeof item === "string" && item.length > 0,
  );
  return out.length > 0 ? out : null;
}

export function compareSemverDesc(a: string, b: string): number {
  const pa = a.split(".").map((part) => Number.parseInt(part, 10) || 0);
  const pb = b.split(".").map((part) => Number.parseInt(part, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const d = (pb[i] ?? 0) - (pa[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export function normalizePhoneReleaseManifest(
  raw: RawPhoneReleaseManifest,
): PhoneReleaseManifest | null {
  const version = asString(raw.version);
  if (!version) return null;
  return {
    version,
    notes: asString(raw.notes) ?? "",
    date: asString(raw.releasedAt) ?? asString(raw.createdAt),
    validatorId: asString(raw.validatorId),
    locales: asStringArray(raw.locales),
    raw: raw as Record<string, unknown>,
  };
}

export function sortPhoneReleaseManifests(
  items: PhoneReleaseManifest[],
): PhoneReleaseManifest[] {
  return [...items].sort((a, b) => compareSemverDesc(a.version, b.version));
}
